import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Shell } from "@/components/Shell";
import { money, fmtDate, DOC_META } from "@/lib/format";
import { MoneyRadar } from "@/components/MoneyRadar";
import { FileText, Receipt, Recycle, Plus, ArrowRight, Clock, Lock, MoreVertical, Mic, Sparkles } from "lucide-react";

const TrialBanner = () => {
  const { access } = useAuth();
  const navigate = useNavigate();
  if (!access) return null;
  if (access.premium) return null;
  if (access.trial_active) {
    return (
      <div data-testid="trial-banner" onClick={() => navigate("/account")} className="bg-onSurface text-onBrand rounded-[16px] p-4 flex items-center gap-3 cursor-pointer active:scale-[0.99] transition-transform">
        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><Clock size={20} /></div>
        <div className="flex-1">
          <p className="font-heading font-bold text-sm">Free trial · Day {access.trial_day} of {access.trial_days_total}</p>
          <p className="text-xs text-white/70">{access.trial_days_left} day{access.trial_days_left === 1 ? "" : "s"} left · then £12/month</p>
        </div>
        <ArrowRight size={18} className="text-white/70" />
      </div>
    );
  }
  return (
    <div data-testid="paywall-banner" onClick={() => navigate("/paywall")} className="bg-brandPrimary text-onBrand rounded-[16px] p-4 flex items-center gap-3 cursor-pointer active:scale-[0.99] transition-transform">
      <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><Lock size={20} /></div>
      <div className="flex-1">
        <p className="font-heading font-bold text-sm">Your trial has ended</p>
        <p className="text-xs text-white/85">Subscribe for £12/month to keep going</p>
      </div>
      <ArrowRight size={18} />
    </div>
  );
};

const QuickAction = ({ icon: Icon, label, color, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="flex-1 bg-surface rounded-[16px] border border-border p-4 flex flex-col items-start gap-3 hover:border-brandPrimary transition-colors active:scale-[0.97]">
    <div className="w-11 h-11 rounded-[12px] flex items-center justify-center" style={{ background: color + "22" }}>
      <Icon size={22} style={{ color }} />
    </div>
    <div className="flex items-center gap-1">
      <Plus size={14} className="text-muted-foreground" />
      <span className="font-heading font-bold text-sm text-onSurface">{label}</span>
    </div>
  </button>
);

export const DocRow = ({ d, onClick, onMenu }) => {
  const m = DOC_META[d.type] || {};
  const total = d.data?.total;
  return (
    <div className="w-full bg-surface rounded-[12px] border border-border flex items-center hover:border-brandPrimary transition-colors overflow-hidden">
      <button data-testid={`doc-row-${d.id}`} onClick={onClick} className="flex-1 min-w-0 flex items-center gap-3 p-3.5 active:opacity-70 transition-opacity text-left">
        <div className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0" style={{ background: (m.color || "#999") + "1a" }}>
          <span className="text-[10px] font-extrabold" style={{ color: m.color }}>{m.prefix}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-sm text-onSurface truncate">{d.client?.name || "No client"}</p>
          <p className="text-xs text-muted-foreground truncate">{d.number} · {d.data?.title || m.label} · {fmtDate(d.created_at)}</p>
        </div>
        {total != null && d.type !== "waste_note" && <span className="font-heading font-bold text-sm text-onSurface">{money(total)}</span>}
        <StatusPill status={d.status} />
      </button>
      {onMenu && (
        <button data-testid={`doc-menu-${d.id}`} onClick={() => onMenu(d)} aria-label="Document options" className="shrink-0 w-11 self-stretch flex items-center justify-center text-muted-foreground hover:text-onSurface hover:bg-surfaceTertiary border-l border-border active:scale-90 transition-all">
          <MoreVertical size={18} />
        </button>
      )}
    </div>
  );
};

export const StatusPill = ({ status }) => {
  const map = { draft: ["#6B7280", "#F3F4F6"], accepted: ["#166534", "#DCFCE7"], unpaid: ["#CA8A04", "#FEF9C3"], paid: ["#166534", "#DCFCE7"], sent: ["#C2410C", "#FFEDD5"] };
  if (!status) return null;
  const [c, bg] = map[status] || map.draft;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full capitalize" style={{ color: c, background: bg }}>{status}</span>;
};

export default function Home() {
  const navigate = useNavigate();
  const { user, access } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDocs = () => api.get("/documents").then(({ data }) => setDocs(data.documents)).finally(() => setLoading(false));
  useEffect(() => { loadDocs(); }, []);

  const create = (type) => {
    if (access && !access.can_create_documents) { navigate("/paywall"); return; }
    navigate(`/new/${type}`);
  };

  const goVoice = () => {
    if (access && !access.can_use_kai) { navigate("/paywall"); return; }
    navigate("/kai", { state: { voice: true } });
  };

  return (
    <Shell showNav title={null}>
      <div className="animate-fade-up">
        <div className="flex items-center justify-between mb-1">
          <div>
            <p className="text-sm text-muted-foreground">Welcome back</p>
            <h1 className="font-heading text-2xl font-extrabold text-onSurface">{user?.business_name || user?.name}</h1>
          </div>
          <button data-testid="admin-logo-btn" onClick={() => navigate("/admin")} className="active:scale-90 transition-transform" aria-label="Docket">
            <img src="/icon-192.png" className="w-10 h-10 rounded-[10px]" alt="" />
          </button>
        </div>
      </div>

      <div className="mt-4"><TrialBanner /></div>

      <button data-testid="home-voice-kai" onClick={goVoice} className="w-full mt-4 bg-surface border border-border rounded-[16px] p-4 flex items-center gap-3.5 hover:border-brandPrimary transition-colors active:scale-[0.99]">
        <span className="relative w-12 h-12 rounded-full bg-brandPrimary text-onBrand flex items-center justify-center shrink-0">
          <span className="absolute inset-0 rounded-full bg-brandPrimary/30 animate-ping" style={{ animationDuration: "2.2s" }} />
          <Mic size={22} className="relative" />
        </span>
        <div className="flex-1 text-left">
          <p className="font-heading font-extrabold text-sm text-onSurface flex items-center gap-1.5">Voice the job <Sparkles size={13} className="text-brandPrimary" /></p>
          <p className="text-xs text-muted-foreground">Tap &amp; talk — Kai drafts your quote or invoice</p>
        </div>
        <ArrowRight size={18} className="text-muted-foreground shrink-0" />
      </button>

      <MoneyRadar onChanged={loadDocs} />

      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mt-6 mb-3">Create new</p>
      <div className="flex gap-3">
        <QuickAction testid="create-quote" icon={FileText} label="Quote" color="#E3592A" onClick={() => create("quote")} />
        <QuickAction testid="create-invoice" icon={Receipt} label="Invoice" color="#166534" onClick={() => create("invoice")} />
        {access?.can_waste_note && <QuickAction testid="create-waste" icon={Recycle} label="Waste note" color="#CA8A04" onClick={() => create("waste_note")} />}
      </div>

      <button data-testid="home-view-documents" onClick={() => navigate("/documents")} className="w-full mt-4 bg-surface border border-border rounded-[14px] p-4 flex items-center gap-3 hover:border-brandPrimary transition-colors active:scale-[0.99]">
        <div className="w-10 h-10 rounded-[10px] bg-brandTertiary flex items-center justify-center"><FileText size={20} className="text-brandSecondary" /></div>
        <div className="flex-1 text-left">
          <p className="font-heading font-bold text-sm text-onSurface">Your documents</p>
          <p className="text-xs text-muted-foreground">{docs.length} document{docs.length === 1 ? "" : "s"} · view, edit & organise</p>
        </div>
        <ArrowRight size={18} className="text-muted-foreground" />
      </button>
    </Shell>
  );
}
